import { useMemo, useState } from "react";
import { money } from "@/lib/format";
import { stockStatusLabel } from "@/lib/inventory";
import {
  aggregateStockLines,
  costDataStatusLabel,
  formatCostAmount,
  formatCostValue,
  formatSellingAmount,
  groupStockByProduct,
  type StockGrain,
  type StockProductGroup,
  type StockValuationLine,
} from "@/lib/inventory-valuation";

function CostBadge({ status }: { status: "known" | "missing" }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
      status === "missing" ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-800"
    }`}>
      {status === "missing" ? "Missing cost / Incomplete valuation" : costDataStatusLabel(status)}
    </span>
  );
}

function StockState({ status }: { status: "ok" | "low" | "out" }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
      status === "out" ? "bg-rose-50 text-rose-800"
      : status === "low" ? "bg-amber-50 text-amber-800"
      : "bg-slate-100 text-slate-700"
    }`}>
      {stockStatusLabel(status)}
    </span>
  );
}

function FlatRows({ rows }: { rows: StockValuationLine[] }) {
  return (
    <>
      {rows.map((row) => (
        <tr key={row.key} className="border-b border-slate-50 hover:bg-slate-50/50">
          <td className="px-4 py-3 text-sm font-medium text-slate-800">{row.productName}</td>
          <td className="px-4 py-3">
            <p className="text-sm text-slate-700">{row.variantLabel}</p>
            {row.variantSku && row.variantSku !== row.variantLabel && (
              <p className="text-xs text-slate-400 font-mono">{row.variantSku}</p>
            )}
          </td>
          <td className="px-4 py-3 text-sm text-slate-600">{row.locationName}</td>
          <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{row.onHand}</td>
          <td className={`px-4 py-3 text-sm text-right ${row.costStatus === "missing" ? "text-amber-700" : "text-slate-800"}`}>
            {formatCostAmount(row.unitCost, row.costStatus, money)}
          </td>
          <td className={`px-4 py-3 text-sm text-right ${row.costStatus === "missing" ? "text-amber-700" : "text-slate-800"}`}>
            {formatCostValue(row.costValue, row.costStatus, money)}
          </td>
          <td className="px-4 py-3 text-sm text-slate-800 text-right">{formatSellingAmount(row.sellingPrice, money)}</td>
          <td className="px-4 py-3 text-sm text-slate-800 text-right">{formatSellingAmount(row.retailValue, money)}</td>
          <td className="px-4 py-3"><CostBadge status={row.costStatus} /></td>
        </tr>
      ))}
    </>
  );
}

function ProductGroups({
  groups,
}: {
  groups: StockProductGroup[];
}) {
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});
  const [expandedLocations, setExpandedLocations] = useState<Record<string, boolean>>({});

  const toggleProduct = (id: string) => {
    setExpandedProducts((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleLocations = (key: string) => {
    setExpandedLocations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {groups.map((product) => {
        const open = Boolean(expandedProducts[product.productId]);
        return (
          <tbody key={product.productId}>
            <tr className="border-b border-slate-50 hover:bg-slate-50/50">
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-slate-800">{product.productName}</p>
                {product.variantCount > 1 && (
                  <button
                    type="button"
                    onClick={() => toggleProduct(product.productId)}
                    className="mt-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    aria-expanded={open}
                  >
                    {open ? "Hide variants" : "Expand variants"}
                    <span className="text-slate-400 font-normal"> · {product.variantCount}</span>
                  </button>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{product.onHand}</td>
              <td className="px-4 py-3"><CostBadge status={product.costStatus} /></td>
              <td className={`px-4 py-3 text-sm text-right ${product.costStatus === "missing" ? "text-amber-700" : "text-slate-800"}`}>
                {formatCostValue(product.costValue, product.costStatus, money)}
              </td>
              <td className="px-4 py-3 text-sm text-slate-800 text-right">{formatSellingAmount(product.retailValue, money)}</td>
              <td className="px-4 py-3 text-sm text-slate-800 text-right">
                {product.potentialGrossMargin == null ? "—" : money(product.potentialGrossMargin)}
              </td>
              <td className="px-4 py-3"><StockState status={product.stockStatus} /></td>
            </tr>
            {open && (
              <tr className="border-b border-slate-100">
                <td colSpan={7} className="px-0 py-0 bg-slate-50/50">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left px-4 py-2 pl-8 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">SKU</th>
                        <th className="text-left px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Variant / options</th>
                        <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
                        <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Cost / unit</th>
                        <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Cost value</th>
                        <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Selling price</th>
                        <th className="text-right px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Retail value</th>
                        <th className="text-left px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Stock state</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.variants.map((variant) => {
                        const locOpen = Boolean(expandedLocations[variant.key]);
                        return (
                          <FragmentRows
                            key={variant.key}
                            variant={variant}
                            locOpen={locOpen}
                            onToggleLocations={() => toggleLocations(variant.key)}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </tbody>
        );
      })}
    </>
  );
}

function FragmentRows({
  variant,
  locOpen,
  onToggleLocations,
}: {
  variant: StockProductGroup["variants"][number];
  locOpen: boolean;
  onToggleLocations: () => void;
}) {
  return (
    <>
      <tr className="border-b border-slate-50">
        <td className="px-4 py-2.5 pl-8">
          <p className="text-sm font-mono text-slate-700">{variant.sku || "—"}</p>
          {variant.locations.length > 1 && (
            <button
              type="button"
              onClick={onToggleLocations}
              className="mt-1 text-xs font-medium text-slate-500 hover:text-slate-700"
              aria-expanded={locOpen}
            >
              {locOpen ? "Hide locations" : "Show locations"}
              <span className="font-normal"> · {variant.locations.length}</span>
            </button>
          )}
        </td>
        <td className="px-4 py-2.5">
          <p className="text-sm text-slate-700">{variant.label}</p>
          {variant.salesQty != null && variant.salesQty > 0 && (
            <p className="text-[11px] text-slate-400 mt-0.5">Period sales {variant.salesQty}</p>
          )}
        </td>
        <td className="px-4 py-2.5 text-sm text-slate-800 text-right">{variant.onHand}</td>
        <td className={`px-4 py-2.5 text-sm text-right ${variant.costStatus === "missing" ? "text-amber-700" : "text-slate-800"}`}>
          {formatCostAmount(variant.unitCost, variant.costStatus, money)}
        </td>
        <td className={`px-4 py-2.5 text-sm text-right ${variant.costStatus === "missing" ? "text-amber-700" : "text-slate-800"}`}>
          {formatCostValue(variant.costValue, variant.costStatus, money)}
        </td>
        <td className="px-4 py-2.5 text-sm text-slate-800 text-right">{formatSellingAmount(variant.sellingPrice, money)}</td>
        <td className="px-4 py-2.5 text-sm text-slate-800 text-right">{formatSellingAmount(variant.retailValue, money)}</td>
        <td className="px-4 py-2.5"><StockState status={variant.stockStatus} /></td>
      </tr>
      {locOpen && variant.locations.map((loc) => (
        <tr key={loc.key} className="border-b border-slate-50 bg-white">
          <td className="px-4 py-2 pl-12 text-xs text-slate-400" colSpan={1} />
          <td className="px-4 py-2 text-sm text-slate-600">{loc.locationName}</td>
          <td className="px-4 py-2 text-sm text-slate-700 text-right">{loc.onHand}</td>
          <td className={`px-4 py-2 text-sm text-right ${loc.costStatus === "missing" ? "text-amber-700" : "text-slate-700"}`}>
            {formatCostAmount(loc.unitCost, loc.costStatus, money)}
          </td>
          <td className={`px-4 py-2 text-sm text-right ${loc.costStatus === "missing" ? "text-amber-700" : "text-slate-700"}`}>
            {formatCostValue(loc.costValue, loc.costStatus, money)}
          </td>
          <td className="px-4 py-2 text-sm text-slate-700 text-right">{formatSellingAmount(loc.sellingPrice, money)}</td>
          <td className="px-4 py-2 text-sm text-slate-700 text-right">{formatSellingAmount(loc.retailValue, money)}</td>
          <td className="px-4 py-2" />
        </tr>
      ))}
    </>
  );
}

export function StockInventoryValueTable({
  grain,
  lines,
  reorderPointByProduct,
  salesQtyByVariant,
}: {
  grain: StockGrain;
  lines: StockValuationLine[];
  reorderPointByProduct?: Record<string, number>;
  salesQtyByVariant?: Record<string, number>;
}) {
  const stockLines = useMemo(() => aggregateStockLines(lines, grain), [lines, grain]);
  const productGroups = useMemo(
    () => groupStockByProduct(lines, { reorderPointByProduct, salesQtyByVariant }),
    [lines, reorderPointByProduct, salesQtyByVariant],
  );

  if (lines.length === 0) {
    return <div className="px-4 py-8 text-center text-sm text-slate-400">No on-hand stock in this snapshot.</div>;
  }

  if (grain === "product") {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/70 border-b border-slate-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost-data status</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Inventory value at cost</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Retail value</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Potential gross margin</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Stock state</th>
            </tr>
          </thead>
          <ProductGroups groups={productGroups} />
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50/70 border-b border-slate-100">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Variant / SKU</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit cost</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost value</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Selling price</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Retail value</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost-data status</th>
          </tr>
        </thead>
        <tbody>
          <FlatRows rows={stockLines} />
        </tbody>
      </table>
    </div>
  );
}
